/**
 * Tenant deletion-request ledger (TMMPDM-0013); Erase now on a pending request (TMMPDM-0092) and a
 * retry on a failed one, with its last error code in words (TMMPDM-0093, tmm-api 0158).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, ErrorState, useNotify } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, type ErrorTranslate } from '@/api/errors';
import { DELETION_ERROR_CODES, type AdminDeletionRow } from '@/api/tmm/types';
import { formatDate } from '@/format';
import { RequestDeletionDialog } from './RequestDeletionDialog';
import { CancelDeletionDialog } from './CancelDeletionDialog';
import { StatusChip } from '@/status';

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set(DELETION_ERROR_CODES);

/**
 * The request's last error in words (TMM-B3), from `lastErrorCode` (tmm-api 0160 U4: a closed code
 * written beside `lastError`, never parsed from it). `UNKNOWN`, a value this console has no words
 * for, and a missing code all read "Erasure failed."
 */
function lastErrorWords(code: string | undefined, t: ErrorTranslate): string {
  return code !== undefined && code !== 'UNKNOWN' && KNOWN_ERROR_CODES.has(code)
    ? t(`deletions.errorCode.${code}`, 'Erasure failed.')
    : t('deletions.errorCode.UNKNOWN', 'Erasure failed.');
}

interface DeletionRequestsPanelProps {
  slug: string;
}

/** What a person copies for support: the request's identifiers and the raw `lastError` and its code (English `key: value` lines; an unsent value is `-`). */
function deletionSupportDetails(row: AdminDeletionRow): string {
  const value = (v: string | number | undefined) => (v === undefined || v === '' ? '-' : String(v));
  return [
    `deletionRequestId: ${row.id}`,
    `userId: ${row.userId}`,
    `status: ${row.status}`,
    `requestedAt: ${row.requestedAt}`,
    `attempts: ${row.attempts}`,
    `lastError: ${value(row.lastError)}`,
    `lastErrorCode: ${value(row.lastErrorCode)}`,
  ].join('\n');
}

export function DeletionRequestsPanel({ slug }: DeletionRequestsPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<AdminDeletionRow[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [cancelling, setCancelling] = useState<AdminDeletionRow | null>(null);
  const [erasing, setErasing] = useState<AdminDeletionRow | null>(null);
  const [eraseBusy, setEraseBusy] = useState(false);
  const [eraseError, setEraseError] = useState<string | null>(null);
  // 409 / 404: the request can no longer be erased, so the confirm offers only Close.
  const [eraseGone, setEraseGone] = useState(false);
  // 202 `retrying: false`: the request failed again, so the confirm offers Try again and Close.
  const [failedAgain, setFailedAgain] = useState(false);

  const reload = () => setReloadToken((n) => n + 1);

  const openErase = (row: AdminDeletionRow) => {
    setEraseError(null);
    setEraseGone(false);
    setFailedAgain(false);
    setErasing(row);
  };

  const eraseNow = async (row: AdminDeletionRow) => {
    setEraseBusy(true);
    setEraseError(null);
    try {
      const result = await adminApi.eraseDeletionNow(slug, row.id);
      if (result.erased) {
        setErasing(null);
        notify.success(t('deletions.erase.erased', 'Account erased.'));
      } else if (result.retrying === true) {
        setErasing(null);
        notify.success(
          t('deletions.erase.retrying', 'Erasure started; it will finish in the background.'),
        );
      } else {
        // 202 `retrying: false`: the request has just gone FAILED and no job finishes it, but erase-now
        // again retries it (tmm-api 0158). The reason is the row's own last error, so re-read the list.
        const fresh = await adminApi
          .listDeletionRequests(slug)
          .then((list) => {
            setRows(list.requests);
            setError(null);
            return list.requests.find((r) => r.id === row.id);
          })
          .catch(() => {
            reload();
            return undefined;
          });
        setFailedAgain(true);
        setEraseError(
          t('deletions.erase.failedAgain', 'The erasure failed again: {{reason}}.', {
            reason: lastErrorWords(fresh?.lastErrorCode, t).replace(/\.$/, ''),
          }),
        );
        return;
      }
      reload();
    } catch (err: unknown) {
      const code = err instanceof ApiError ? String(err.code) : '';
      if (code === 'DELETION_CANCELLED') {
        setEraseGone(true);
        setEraseError(
          t('deletions.erase.cancelledError', "This request was cancelled, so it can't be erased."),
        );
        reload();
      } else if (code === 'DELETION_REQUEST_NOT_FOUND') {
        setEraseGone(true);
        setEraseError(t('deletions.erase.notFoundError', 'This request no longer exists.'));
        reload();
      } else {
        setEraseError(errorCopy(err, t, t('deletions.erase.error', "Couldn't erase the account.")));
      }
    } finally {
      setEraseBusy(false);
    }
  };

  const copyDetails = async (row: AdminDeletionRow) => {
    try {
      await navigator.clipboard.writeText(deletionSupportDetails(row));
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listDeletionRequests(slug)
      .then((list) => {
        if (cancelled) return;
        setRows(list.requests);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, reloadToken]);

  const sorted = useMemo(() => {
    if (!rows) return null;
    return [...rows].sort((a, b) => (a.requestedAt < b.requestedAt ? 1 : -1));
  }, [rows]);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('deletions.loadError', 'Could not load deletion requests.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={() => setRequestOpen(true)}>
          {t('deletions.request.action', 'Request deletion…')}
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('deletions.tableAria', 'Deletion requests')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('deletions.col.person', 'Person')}</TableCell>
              <TableCell>{t('deletions.col.requested', 'Requested')}</TableCell>
              <TableCell>{t('deletions.col.coolingOff', 'Cooling-off until')}</TableCell>
              <TableCell>{t('deletions.col.status', 'Status')}</TableCell>
              <TableCell>{t('deletions.col.erasedAt', 'Erased at')}</TableCell>
              <TableCell>{t('deletions.col.attempts', 'Attempts')}</TableCell>
              <TableCell>{t('deletions.col.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted === null ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('common.loading', 'Loading')}
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <EmptyState title={t('deletions.empty', 'No deletion requests.')} />
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row) => {
                // TMM-B3: the raw `lastError` is only copied for support, never shown. UI-010: the
                // "failed" line is the row's status alone; Copy details is offered by `lastError`.
                const attemptsCell = (
                  <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                    <Typography variant="body2" component="span">
                      {row.attempts}
                    </Typography>
                    {row.status === 'FAILED' ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('components.lastAttemptFailed', 'The last attempt failed.')}
                      </Typography>
                    ) : null}
                    {row.lastError ? (
                      <Button
                        size="small"
                        variant="outlined"
                        aria-label={t(
                          'deletions.copyDetailsAria',
                          'Copy details for support for deletion request {{id, number}}',
                          { id: row.id },
                        )}
                        onClick={() => void copyDetails(row)}
                      >
                        {t('components.copyDetails', 'Copy details for support')}
                      </Button>
                    ) : null}
                  </Stack>
                );
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2">{row.displayName ?? '—'}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {row.userId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(row.requestedAt, locale)}</Typography>
                      {row.requestedBy ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {t('deletions.requestedBy', 'by {{who}}', { who: row.requestedBy })}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{formatDate(row.coolingOffUntil, locale)}</TableCell>
                    <TableCell>
                      <StatusChip
                        status={row.status}
                        label={t(`deletions.status.${row.status}`, row.status)}
                      />
                      {/* TMM-B3: the code in words beside a failed row's status; the raw text is copy-only. */}
                      {row.status === 'FAILED' && row.lastError ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {lastErrorWords(row.lastErrorCode, t)}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{formatDate(row.erasedAt, locale)}</TableCell>
                    <TableCell>{attemptsCell}</TableCell>
                    <TableCell>
                      {/* The composition rule: the row's own status alone decides its actions. */}
                      {row.status === 'PENDING' || row.status === 'FAILED' ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {row.status === 'PENDING' ? (
                            <Button size="small" onClick={() => setCancelling(row)}>
                              {t('deletions.cancel', 'Cancel…')}
                            </Button>
                          ) : null}
                          <Button size="small" color="error" onClick={() => openErase(row)}>
                            {t('deletions.erase.action', 'Erase now…')}
                          </Button>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <RequestDeletionDialog
        open={requestOpen}
        slug={slug}
        onClose={() => setRequestOpen(false)}
        onDone={() => {
          setRequestOpen(false);
          notify.success(t('deletions.requested', 'Deletion requested.'));
          setReloadToken((n) => n + 1);
        }}
      />
      <CancelDeletionDialog
        open={cancelling !== null}
        slug={slug}
        requestId={cancelling?.id ?? 0}
        person={cancelling ? cancelling.displayName || String(cancelling.userId) : ''}
        onClose={() => setCancelling(null)}
        onDone={() => {
          setCancelling(null);
          notify.success(t('deletions.cancelled', 'Deletion request cancelled.'));
          setReloadToken((n) => n + 1);
        }}
      />
      <ConfirmDialog
        open={erasing !== null}
        title={
          erasing?.status === 'FAILED'
            ? t('deletions.erase.retryTitle', 'Try erasing this account again?')
            : t('deletions.erase.title', 'Erase this account now?')
        }
        consequence={
          erasing?.status === 'FAILED'
            ? t(
                'deletions.erase.retryBody',
                "The last attempt failed. This tries again now, and can't be undone once it succeeds.",
              )
            : t(
                'deletions.erase.body',
                "This permanently deletes {{person}}'s data now instead of at the scheduled time. It can't be undone.",
                { person: erasing ? erasing.displayName || String(erasing.userId) : '' },
              )
        }
        confirmLabel={
          failedAgain
            ? t('common.tryAgain', 'Try again')
            : t('deletions.erase.confirm', 'Erase now')
        }
        {...(eraseGone || failedAgain ? { cancelLabel: t('common.close', 'Close') } : {})}
        destructive
        confirmDisabled={eraseGone}
        busy={eraseBusy}
        error={eraseError}
        onConfirm={() => {
          if (erasing) void eraseNow(erasing);
        }}
        onCancel={() => setErasing(null)}
      />
    </Stack>
  );
}
